import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  FiChevronLeft,
  FiInfo,
  FiSliders,
  FiBarChart2,
  FiShield,
  FiSettings,
  FiMail
} from 'react-icons/fi';

const CookiesPolicyPage = () => {
  const [activeSection, setActiveSection] = useState('');
  const containerRef = useRef(null);

  const sectionRefs = {
    section1: useRef(null),
    section2: useRef(null),
    section3: useRef(null),
    section4: useRef(null),
    section5: useRef(null),
    section6: useRef(null)
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.getAttribute('id'));
          }
        });
      },
      { rootMargin: '-20% 0px -75% 0px' }
    );

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) observer.unobserve(ref.current);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollTo = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) section.scrollIntoView({ behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } }
  };

  const tableOfContents = [
    { id: 'section-1', title: '¿Qué son las cookies?', icon: <FiInfo /> },
    { id: 'section-2', title: 'Cookies que utilizamos', icon: <FiSliders /> },
    { id: 'section-3', title: 'Cookies de terceros', icon: <FiBarChart2 /> },
    { id: 'section-4', title: 'Tus opciones', icon: <FiSettings /> },
    { id: 'section-5', title: 'Privacidad y seguridad', icon: <FiShield /> },
    { id: 'section-6', title: 'Contacto', icon: <FiMail /> }
  ];

  return (
    <>
      <Helmet>
        <title>Política de Cookies | Avlink Network</title>
        <meta
          name="description"
          content="Información sobre el uso de cookies en Avlink Network: qué son, qué tipos usamos, cookies de terceros y cómo configurar tu navegador para administrarlas."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.avlinknetwork.org/legal/cookies" />
      </Helmet>

      <div className="pt-24 pb-20 bg-dark-900" ref={containerRef}>
        {/* Hero */}
        <section className="relative overflow-hidden py-20 mb-16">
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage:
                'radial-gradient(rgba(255, 255, 255, 0.8) 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}
          />
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              className="max-w-4xl mx-auto text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Política de <span className="text-primary-500">Cookies</span>
              </h1>
              <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                Esta política explica qué cookies utiliza Avlink Network, con qué finalidad y cómo
                podés gestionarlas desde tu navegador.
              </p>
              <div className="flex items-center justify-center text-sm text-gray-400">
                <FiInfo className="mr-2 text-primary-500" />
                <span>Última actualización: 11 de mayo de 2026</span>
              </div>
            </motion.div>
          </div>
        </section>

        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Sidebar TOC */}
            <aside className="md:w-1/4 lg:w-1/5">
              <div className="sticky top-24">
                <div className="bg-dark-800/80 backdrop-blur-sm rounded-xl border border-dark-600 p-5 mb-6">
                  <h3 className="text-lg font-bold text-white mb-4">Contenido</h3>
                  <nav>
                    <ul className="space-y-3">
                      {tableOfContents.map((item) => (
                        <li key={item.id}>
                          <button
                            onClick={() => scrollTo(item.id)}
                            className={`w-full flex items-center p-2 rounded-lg text-left transition-colors ${
                              activeSection === item.id
                                ? 'bg-primary-900/30 text-primary-400 border-l-2 border-primary-500 pl-3'
                                : 'text-gray-400 hover:text-white hover:bg-dark-700'
                            }`}
                          >
                            <span className="mr-3 text-primary-500">{item.icon}</span>
                            <span className="text-sm">{item.title}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>

                <Link
                  to="/"
                  className="w-full flex items-center justify-center py-3 px-4 bg-dark-800 hover:bg-dark-700 border border-dark-600 rounded-lg text-gray-300 hover:text-white transition-colors text-sm"
                >
                  <FiChevronLeft className="mr-2" />
                  Volver al inicio
                </Link>
              </div>
            </aside>

            {/* Contenido */}
            <div className="md:w-3/4 lg:w-4/5">
              <div className="bg-dark-800/80 backdrop-blur-sm rounded-xl border border-dark-600 p-6 lg:p-8 mb-12">
                <div className="bg-dark-700/80 border-l-4 border-primary-500 p-4 mb-8">
                  <p className="font-medium text-white">EN POCAS PALABRAS:</p>
                  <p className="text-gray-300">
                    Avlink Network utiliza cookies estrictamente necesarias para el funcionamiento
                    del sitio y la sesión de usuario. Cualquier cookie analítica o de terceros se
                    detalla en esta página y podés gestionarla desde tu navegador.
                  </p>
                </div>

                {/* Sección 1 */}
                <motion.section
                  id="section-1"
                  ref={sectionRefs.section1}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      1
                    </span>
                    <span>¿QUÉ SON LAS COOKIES?</span>
                  </h2>
                  <div className="space-y-4 text-gray-300">
                    <p>
                      Las cookies son pequeños archivos de texto que un sitio web coloca en tu
                      dispositivo (computadora, tablet o teléfono) cuando lo visitás. Permiten que
                      el sitio recuerde tus acciones y preferencias durante un tiempo, para que no
                      tengas que volver a ingresarlas cada vez que navegás entre páginas.
                    </p>
                    <p>
                      Avlink Network también utiliza tecnologías similares como{' '}
                      <strong className="text-white">localStorage</strong> y{' '}
                      <strong className="text-white">sessionStorage</strong> para guardar
                      preferencias y mantener tu sesión iniciada. A los efectos de esta política,
                      todas esas tecnologías reciben el mismo tratamiento que las cookies.
                    </p>
                  </div>
                </motion.section>

                {/* Sección 2 */}
                <motion.section
                  id="section-2"
                  ref={sectionRefs.section2}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      2
                    </span>
                    <span>COOKIES QUE UTILIZAMOS</span>
                  </h2>
                  <p className="mb-4 text-gray-300">
                    Clasificamos las cookies en función de su finalidad:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2 flex items-center">
                        <FiShield className="text-primary-500 mr-2" /> Estrictamente necesarias
                      </h3>
                      <p className="text-gray-300 text-sm">
                        Habilitan funciones básicas: iniciar sesión, mantener la sesión activa,
                        recordar tu última membresía y proteger el sitio contra accesos no
                        autorizados. Sin estas cookies la plataforma no puede funcionar
                        correctamente.
                      </p>
                    </div>

                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2 flex items-center">
                        <FiSettings className="text-primary-500 mr-2" /> Funcionales
                      </h3>
                      <p className="text-gray-300 text-sm">
                        Recuerdan tus preferencias: idioma, aceptación de los Términos y
                        Condiciones, configuraciones del dashboard, filtros guardados y demás
                        ajustes que mejoran tu experiencia.
                      </p>
                    </div>

                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2 flex items-center">
                        <FiBarChart2 className="text-primary-500 mr-2" /> Analíticas
                      </h3>
                      <p className="text-gray-300 text-sm">
                        Nos ayudan a entender cómo se utiliza la plataforma de manera agregada y
                        anónima: páginas visitadas, tiempo en el sitio, dispositivos y orígenes
                        de tráfico. La información no se utiliza para identificarte
                        personalmente.
                      </p>
                    </div>

                    <div className="bg-dark-700/50 p-4 rounded-lg border border-dark-600">
                      <h3 className="font-medium text-white mb-2 flex items-center">
                        <FiSliders className="text-primary-500 mr-2" /> De rendimiento
                      </h3>
                      <p className="text-gray-300 text-sm">
                        Permiten monitorear el desempeño del sitio (tiempos de carga, errores) y
                        detectar problemas técnicos para corregirlos.
                      </p>
                    </div>
                  </div>
                </motion.section>

                {/* Sección 3 */}
                <motion.section
                  id="section-3"
                  ref={sectionRefs.section3}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      3
                    </span>
                    <span>COOKIES DE TERCEROS</span>
                  </h2>
                  <p className="mb-4 text-gray-300">
                    Avlink Network se apoya en proveedores externos para distintos servicios.
                    Estos proveedores pueden establecer sus propias cookies, regidas por sus
                    propias políticas de privacidad:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-gray-300">
                    <li>
                      <strong className="text-white">Supabase</strong> — autenticación,
                      persistencia de sesión y base de datos.
                    </li>
                    <li>
                      <strong className="text-white">Vercel</strong> — hosting, distribución de
                      contenido y métricas de rendimiento.
                    </li>
                    <li>
                      <strong className="text-white">Proveedores de análisis</strong> — si está
                      activo (por ejemplo Google Analytics o Vercel Analytics), recopilan
                      estadísticas agregadas de uso del sitio.
                    </li>
                  </ul>
                  <p className="mt-4 text-gray-300">
                    Te recomendamos revisar las políticas de privacidad y cookies de cada uno
                    de estos servicios directamente en sus sitios oficiales.
                  </p>
                </motion.section>

                {/* Sección 4 */}
                <motion.section
                  id="section-4"
                  ref={sectionRefs.section4}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      4
                    </span>
                    <span>TUS OPCIONES</span>
                  </h2>
                  <p className="mb-4 text-gray-300">
                    Podés controlar las cookies desde la configuración de tu navegador. La
                    mayoría permite:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mb-4 text-gray-300">
                    <li>Ver qué cookies están almacenadas y eliminarlas individualmente.</li>
                    <li>Bloquear cookies de terceros.</li>
                    <li>Bloquear todas las cookies.</li>
                    <li>
                      Configurar que se eliminen automáticamente al cerrar la sesión del
                      navegador.
                    </li>
                  </ul>
                  <p className="text-gray-300">
                    Tené en cuenta que si bloqueás las cookies estrictamente necesarias podés
                    perder funcionalidades clave del sitio (por ejemplo, no vas a poder
                    mantener iniciada tu sesión). Enlaces útiles a las guías oficiales:{' '}
                    <a
                      href="https://support.google.com/chrome/answer/95647"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:underline"
                    >
                      Chrome
                    </a>
                    ,{' '}
                    <a
                      href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:underline"
                    >
                      Firefox
                    </a>
                    ,{' '}
                    <a
                      href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:underline"
                    >
                      Safari
                    </a>
                    ,{' '}
                    <a
                      href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:underline"
                    >
                      Edge
                    </a>
                    .
                  </p>
                </motion.section>

                {/* Sección 5 */}
                <motion.section
                  id="section-5"
                  ref={sectionRefs.section5}
                  className="mb-12"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      5
                    </span>
                    <span>PRIVACIDAD Y SEGURIDAD</span>
                  </h2>
                  <p className="text-gray-300">
                    El uso de cookies se complementa con nuestra{' '}
                    <Link to="/legal/privacy" className="text-primary-400 hover:underline">
                      Política de Privacidad
                    </Link>
                    , donde detallamos qué datos personales tratamos, con qué finalidad y bajo
                    qué bases legales. Avlink Network nunca vende los datos personales de sus
                    usuarios.
                  </p>
                </motion.section>

                {/* Sección 6 */}
                <motion.section
                  id="section-6"
                  ref={sectionRefs.section6}
                  className="mb-4"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={fadeInUp}
                >
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <span className="bg-primary-900/30 text-primary-400 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      6
                    </span>
                    <span>CONTACTO</span>
                  </h2>
                  <p className="text-gray-300">
                    Si tenés dudas sobre nuestra política de cookies, escribinos a través del{' '}
                    <Link to="/contact" className="text-primary-400 hover:underline">
                      formulario de contacto
                    </Link>{' '}
                    o al correo{' '}
                    <a
                      href="mailto:contacto@avlink-network.com"
                      className="text-primary-400 hover:underline"
                    >
                      contacto@avlink-network.com
                    </a>
                    .
                  </p>
                </motion.section>
              </div>

              <div className="text-center text-gray-500 text-sm">
                © {new Date().getFullYear()} Avlink Network. Todos los derechos reservados.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiesPolicyPage;
